"use client";

import { useState, useMemo } from "react";
import "./MyCertificatesPage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MyCertificatesPage() {
  const [documentNumber, setDocumentNumber] = useState("");
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("all");
  const [fadeOut, setFadeOut] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  const handleDocumentInputChange = (event) => {
    setDocumentNumber(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!documentNumber.trim()) {
      setError("Por favor ingrese un número de documento");
      return;
    }

    setIsLoading(true);
    setError("");
    setFadeOut(true);

    setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/participants/certificates/${documentNumber}`
        );

        if (!response.ok) {
          let errorMessage =
            "Error al obtener los certificados. Por favor, intente de nuevo.";
          const contentType = response.headers.get("content-type");

          if (contentType && contentType.indexOf("application/json") !== -1) {
            const errorData = await response.json();
            if (response.status === 404) {
              errorMessage =
                errorData.message ||
                "No se encontró ningún participante con ese número de documento.";
            } else if (response.status === 500) {
              errorMessage =
                errorData.message ||
                "Ocurrió un error en el servidor. Por favor, intente más tarde.";
            } else {
              errorMessage =
                errorData.message ||
                `Error: ${response.status} ${response.statusText}`;
            }
          } else {
            errorMessage = `El servidor respondió con un formato inesperado (${response.status}).`;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setCertificates(data);
        setSearched(true);
        setFadeIn(true);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching certificates:", err);
        setFadeOut(false);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const filteredCertificates = useMemo(() => {
    if (!searchTerm) {
      return certificates;
    }
    return certificates.filter((cert) => {
      let value = "";
      if (searchColumn === "all") {
        value = `${cert.event_name} ${cert.organizer_name} ${cert.event_type_name}`;
      } else if (searchColumn === "event_name") {
        value = String(cert.event_name || "");
      } else if (searchColumn === "organizer_name") {
        value = String(cert.organizer_name || "");
      } else if (searchColumn === "event_type_name") {
        value = String(cert.event_type_name || "");
      }

      return value.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [certificates, searchTerm, searchColumn]);

  const getDownloadUrl = (certificateId) => {
    return `${API_BASE_URL}/participants/certificates/${certificateId}/generate`;
  };

  const resetSearch = () => {
    setFadeOut(true); // Usamos el mismo efecto que al buscar
    setTimeout(() => {
      setDocumentNumber("");
      setCertificates([]);
      setSearched(false);
      setSearchTerm("");
      setError("");
      setFadeIn(false);
      setFadeOut(false);
    }, 300);
  };

  return (
    <div className="certificates-container">
      <div className="certificates-content">
        {!searched ? (
          <div className={`initial-view ${fadeOut ? "fade-out" : ""}`}>
            <div className="form-and-help-container">
              <div className="form-card">
                <div className="form-header">
                  <h2 className="certificates-title">Mis Certificados</h2>
                  <p className="certificates-description">
                    Ingrese su número de documento para buscar sus certificados
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="certificates-form-document-search"
                >
                  <input
                    type="text"
                    placeholder="Número de documento"
                    value={documentNumber}
                    onChange={handleDocumentInputChange}
                    className="certificates-input"
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="certificates-button"
                  >
                    {isLoading ? "Buscando..." : "Buscar Certificados"}
                  </button>
                </form>
                {error && <p className="error-message">{error}</p>}
              </div>

              <div className="help-steps">
                <h3>¿Cómo obtener tus certificados?</h3>
                <div className="steps">
                  <div className="step">
                    <div className="step-number-container">
                      <div className="step-number">1</div>
                    </div>
                    <div className="step-content">
                      <h4>Ingresa tu documento</h4>
                      <p>
                        Escribe tu número de cédula o documento de identidad
                      </p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number-container">
                      <div className="step-number">2</div>
                    </div>
                    <div className="step-content">
                      <h4>Busca tus certificados</h4>
                      <p>Haz clic en "Buscar Certificados" para ver tu lista</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number-container">
                      <div className="step-number">3</div>
                    </div>
                    <div className="step-content">
                      <h4>Descarga y comparte</h4>
                      <p>
                        Descarga tus certificados y compártelos donde necesites
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="certificate-animation">
              <div className="certificate-icon">
                <svg viewBox="0 0 200 200" className="certificate-svg">
                  <defs>
                    <linearGradient
                      id="certGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#1a3d6e" />
                      <stop offset="50%" stopColor="#1f74b9" />
                      <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="20"
                    y="30"
                    width="160"
                    height="120"
                    rx="8"
                    fill="url(#certGradient)"
                    opacity="0.1"
                  />
                  <rect
                    x="25"
                    y="35"
                    width="150"
                    height="110"
                    rx="6"
                    fill="url(#certGradient)"
                    opacity="0.2"
                  />
                  <rect
                    x="30"
                    y="40"
                    width="140"
                    height="100"
                    rx="4"
                    fill="url(#certGradient)"
                  />
                  <circle cx="100" cy="90" r="15" fill="white" opacity="0.9" />
                  <path
                    d="M95 90 L98 93 L105 86"
                    stroke="#1a3d6e"
                    strokeWidth="2"
                    fill="none"
                  />
                  <rect
                    x="40"
                    y="110"
                    width="60"
                    height="3"
                    fill="white"
                    opacity="0.7"
                  />
                  <rect
                    x="40"
                    y="118"
                    width="80"
                    height="2"
                    fill="white"
                    opacity="0.5"
                  />
                  <rect
                    x="40"
                    y="124"
                    width="50"
                    height="2"
                    fill="white"
                    opacity="0.5"
                  />
                </svg>
              </div>
              <div className="floating-elements">
                <div className="floating-element element-1">📜</div>
                <div className="floating-element element-2">🎓</div>
                <div className="floating-element element-3">✨</div>
                <div className="floating-element element-4">🏆</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`results-view ${fadeIn ? "fade-in" : ""}`}>
            <div className="new-search-container">
              <button
                type="button"
                onClick={resetSearch}
                className="new-search-button"
              >
                <span className="icon">←</span> Nueva Búsqueda
              </button>
            </div>

            {certificates.length > 0 && (
              <div className="certificates-results">
                <div className="search-within-results">
                  <select
                    value={searchColumn}
                    onChange={(e) => setSearchColumn(e.target.value)}
                    className="search-column-select"
                  >
                    <option value="all">Todos los campos</option>
                    <option value="event_name">Nombre del Evento</option>
                    <option value="organizer_name">Organizador</option>
                    <option value="event_type_name">Tipo de Evento</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Buscar en mis certificados..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="certificate-search-input"
                  />
                </div>

                <div className="table-container">
                  <table className="certificates-table">
                    <thead>
                      <tr>
                        <th>Evento</th>
                        <th>Tipo</th>
                        <th>Organizador</th>
                        <th>Horas</th>
                        <th>Trabajo Presentado</th>
                        <th>Fecha</th>
                        <th className="actions-column">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCertificates.map((certificate) => (
                        <tr key={certificate.certificate_id}>
                          <td>{certificate.event_name}</td>
                          <td>{certificate.event_type_name || "N/A"}</td>
                          <td>{certificate.organizer_name || "N/A"}</td>
                          <td>{certificate.hours_quantity || "N/A"}</td>
                          <td>{certificate.presented_work || "N/A"}</td>
                          <td>
                            {new Date(
                              certificate.certification_date
                            ).toLocaleDateString("es-ES", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </td>
                          <td className="actions">
                            <a
                              href={getDownloadUrl(certificate.certificate_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-button2"
                            >
                              <span className="download-icon">↓</span>
                              <span className="download-text">Descargar</span>
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isLoading && certificates.length === 0 && (
              <p className="no-results">
                No se encontraron certificados para este número de documento.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyCertificatesPage;
